/**
 * Elevation (wall-focus side view) representation of OPENING_SKETCH
 * annotations.
 *
 * Opening sketches are parametric plan objects — their normal visuals (glyph
 * in the wall sketch's 2D skin, footprint line) are flat at floor height and
 * invisible edge-on. While the door/window tool focuses a wall, this module
 * renders each opening on the focused chain as its true vertical rectangle
 * (jambs at `offset`/`offset+length` along the wall, sill/head at
 * `lowerBound`/`lowerBound+height`) and exposes that rectangle as an
 * {@link EditableShape} so the existing rectangle-only prompt editor can
 * move/resize it.
 */

import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../../../../viewer/viewer';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { pointAtArcLength } from '../../../../utils/skeleton/arcGeometry';
import type { EditableShape, EditableShapeKind, EditEdge } from '../../implementations/line-tool/EditableShape';
import {
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchLowerBoundProperty,
  getOpeningSketchHeightProperty,
} from '@pointorama/potree-shared';

/** Brand selection blue — matches the plan-view opening highlight. */
const RECT_COLOR = new THREE.Color('#269FC6');
const RECT_LINE_WIDTH = 3;
const RECT_RENDER_ORDER = 998;

/**
 * The opening's vertical rectangle corners in world space, ordered
 * BL, BR, TR, TL (matching the legacy OpeningAnnotation corner convention).
 * Jamb positions follow the (possibly curved) wall centerline; the rectangle
 * spans their chord. Null when the opening or its edge can't be resolved.
 */
export function openingSketchElevationCorners(skeleton: PointoramaWallSketch, openingId: string): THREE.Vector3[] | null {
  let edgeId = '';
  let offset = 0;
  let length = 0;
  let lower = 0;
  let height = 0;
  try {
    edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
    offset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
    length = getOpeningSketchLengthProperty(openingId).value ?? 0;
    lower = getOpeningSketchLowerBoundProperty(openingId).value ?? 0;
    height = getOpeningSketchHeightProperty(openingId).value ?? 0;
  } catch {
    return null;
  }
  if (!edgeId || length <= 0 || height <= 0) return null;
  const edge = skeleton.getEdge(edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
  const jambA = pointAtArcLength(aXY, bXY, edge.bulge, offset).point;
  const jambB = pointAtArcLength(aXY, bXY, edge.bulge, offset + length).point;
  return [
    new THREE.Vector3(jambA.x, jambA.y, lower),
    new THREE.Vector3(jambB.x, jambB.y, lower),
    new THREE.Vector3(jambB.x, jambB.y, lower + height),
    new THREE.Vector3(jambA.x, jambA.y, lower + height),
  ];
}

/** Opening sketches whose wall edge belongs to the focused chain. */
export function openingSketchIdsOnChain(viewer: Viewer, skeleton: PointoramaWallSketch, edgeIds: readonly string[]): string[] {
  const chain = new Set(edgeIds);
  const ids: string[] = [];
  for (const os of viewer.scene.openingSketches) {
    if (os.groupIdentifier !== skeleton.groupIdentifier) continue;
    try {
      if (chain.has(getOpeningSketchWallEdgeIdProperty(os.identifier).value || '')) ids.push(os.identifier);
    } catch {
      // property service not ready — skip
    }
  }
  return ids;
}

/**
 * Renders the elevation rectangles of every opening sketch on the focused
 * chain into one LineSegments2. Call {@link refresh} per frame — it rebuilds
 * geometry only when the parameter signature changed, so the steady state is
 * a cheap string compare.
 */
export class OpeningSketchElevationOverlay {
  private line: LineSegments2 | null = null;
  private geometry: LineSegmentsGeometry | null = null;
  private material: LineMaterial | null = null;
  private signature = '';
  /** Opening currently in prompt-edit — its static rectangle is suppressed so
   *  only the live edit preview shows. */
  hiddenOpeningId: string | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly getGroup: () => THREE.Group,
  ) {}

  refresh(skeleton: PointoramaWallSketch | null, edgeIds: readonly string[]): void {
    if (!skeleton || edgeIds.length === 0) {
      this.hide();
      return;
    }
    const ids = openingSketchIdsOnChain(this.viewer, skeleton, edgeIds).filter((id) => id !== this.hiddenOpeningId);
    const rects: THREE.Vector3[][] = [];
    const signatureParts: string[] = [];
    for (const id of ids) {
      const corners = openingSketchElevationCorners(skeleton, id);
      if (!corners) continue;
      rects.push(corners);
      signatureParts.push(`${id}:${corners.map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)},${c.z.toFixed(4)}`).join(';')}`);
    }
    if (rects.length === 0) {
      this.hide();
      return;
    }
    const signature = signatureParts.join('|');
    if (signature === this.signature && this.line?.visible) return;
    this.signature = signature;

    const positions: number[] = [];
    for (const corners of rects) {
      for (let i = 0; i < corners.length; i++) {
        const p = corners[i];
        const q = corners[(i + 1) % corners.length];
        positions.push(p.x, p.y, p.z, q.x, q.y, q.z);
      }
    }
    const resolution = new THREE.Vector2(this.viewer.renderer.domElement.clientWidth || 1000, this.viewer.renderer.domElement.clientHeight || 1000);
    if (!this.line) {
      this.geometry = new LineSegmentsGeometry();
      this.geometry.setPositions(positions);
      this.material = new LineMaterial({
        color: RECT_COLOR.getHex(),
        linewidth: RECT_LINE_WIDTH,
        resolution,
        depthTest: false,
        transparent: true,
      });
      this.line = new LineSegments2(this.geometry, this.material);
      this.line.renderOrder = RECT_RENDER_ORDER;
      this.line.computeLineDistances();
      this.getGroup().add(this.line);
    } else {
      // Swap the geometry — LineSegmentsGeometry buffers are fixed at construction.
      const old = this.geometry;
      this.geometry = new LineSegmentsGeometry();
      this.geometry.setPositions(positions);
      this.line.geometry = this.geometry;
      old?.dispose();
      this.line.computeLineDistances();
      this.material?.resolution.copy(resolution);
    }
    this.line.visible = true;
  }

  hide(): void {
    this.signature = '';
    if (this.line) this.line.visible = false;
  }

  dispose(): void {
    this.hiddenOpeningId = null;
    this.signature = '';
    if (this.line) {
      this.line.parent?.remove(this.line);
      this.geometry?.dispose();
      this.material?.dispose();
      this.line = null;
      this.geometry = null;
      this.material = null;
    }
  }
}

/**
 * Pick the opening sketch under the cursor by its ELEVATION rectangle
 * (projected pixel-space point-in-quad). Returns the opening id, or null.
 */
export function pickOpeningSketchRectAtPixel(viewer: Viewer, skeleton: PointoramaWallSketch, edgeIds: readonly string[], mousePx: { x: number; y: number }, camera: THREE.Camera): string | null {
  const width = viewer.renderer.domElement.clientWidth;
  const height = viewer.renderer.domElement.clientHeight;
  for (const id of openingSketchIdsOnChain(viewer, skeleton, edgeIds)) {
    const corners = openingSketchElevationCorners(skeleton, id);
    if (!corners) continue;
    const projected: { x: number; y: number }[] = [];
    let onScreen = true;
    for (const p of corners) {
      const ndc = p.clone().project(camera);
      if (ndc.z < -1 || ndc.z > 1) {
        onScreen = false;
        break;
      }
      projected.push({ x: ((ndc.x + 1) / 2) * width, y: ((1 - ndc.y) / 2) * height });
    }
    if (!onScreen) continue;
    let inside = false;
    for (let i = 0, j = projected.length - 1; i < projected.length; j = i++) {
      const xi = projected[i].x;
      const yi = projected[i].y;
      const xj = projected[j].x;
      const yj = projected[j].y;
      if (yi > mousePx.y !== yj > mousePx.y && mousePx.x < ((xj - xi) * (mousePx.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    if (inside) return id;
  }
  return null;
}

/**
 * An opening sketch's elevation rectangle as an {@link EditableShape} for the
 * LineTool's rectangle-only prompt editor. `enterEditMode` hands out the four
 * edge midpoints and hides the static overlay rectangle; the commit itself is
 * the kind adapter's job (`commitEdit` maps the edited polygon back onto the
 * sketch's parametric encoding).
 */
export class EditableOpeningSketchRect implements EditableShape {
  readonly identifier: string;

  constructor(
    private readonly skeleton: PointoramaWallSketch,
    openingId: string,
    private readonly overlay: OpeningSketchElevationOverlay | null,
  ) {
    this.identifier = openingId;
  }

  getKind(): EditableShapeKind {
    return 'door_window';
  }

  enterEditMode(): EditEdge[] {
    const corners = openingSketchElevationCorners(this.skeleton, this.identifier);
    if (!corners) return [];
    if (this.overlay) this.overlay.hiddenOpeningId = this.identifier;
    const edges: EditEdge[] = [];
    for (let i = 0; i < corners.length; i++) {
      const start = corners[i];
      const end = corners[(i + 1) % corners.length];
      edges.push({
        midpoint: start.clone().add(end).multiplyScalar(0.5),
        direction: end.clone().sub(start).normalize(),
        start: start.clone(),
        end: end.clone(),
      });
    }
    return edges;
  }

  exitEditMode(_confirm: boolean, _newPolygon?: THREE.Vector3[]): void {
    // Nothing to restore on the shape itself — the rectangle re-derives from
    // the (possibly updated) sketch properties. Re-show the static overlay.
    if (this.overlay && this.overlay.hiddenOpeningId === this.identifier) {
      this.overlay.hiddenOpeningId = null;
    }
  }

  getBasePlane(): { position: THREE.Vector3 } {
    const corners = openingSketchElevationCorners(this.skeleton, this.identifier);
    const position = corners ? corners[0].clone().add(corners[2]).multiplyScalar(0.5) : new THREE.Vector3();
    return { position };
  }
}
